import { useMemo } from 'react';

import clsx from 'clsx';

import SidebarCard from '../ui/SidebarCard';
import { useExplorationState } from '../../context/ExplorationContext';
import { computeSidebarStorageMetrics } from '../../lib/sidebar/sidebarStorageMetrics';
import type { ExplorationVolume } from '../../types/exploration';
import { formatBytes } from '../../lib/formatBytes';
import Typography from '~/shared/components/Typography';

import SavingOpportunityCard from './shared/SavingOpportunityCard';

interface SidebarStorageCardProps {
  scoped: ExplorationVolume[];
  /** Hide the view signal metric (Supported/Active) — used at reason-leaf drill depth. */
  singleMetric?: boolean;
}

interface StorageMetricProps {
  title: string;
  storageSize: string;
  volumeCount: string;
}

export default function SidebarStorageCard({
  scoped,
  singleMetric = false,
}: SidebarStorageCardProps) {
  const state = useExplorationState();

  const totalSize = useMemo(() => {
    return scoped.reduce((s, v) => s + (v.size || 0), 0);
  }, [scoped]);
  const metrics = useMemo(() => {
    if (singleMetric) return [];
    return computeSidebarStorageMetrics(scoped, state.view);
  }, [scoped, state.view, singleMetric]);

  return (
    <SidebarCard title="Storage">
      <div
        className={clsx('grid gap-5 pt-3 min-w-0', singleMetric ? 'grid-cols-1' : 'grid-cols-2')}
      >
        {singleMetric ? (
          <StorageMetric
            title="Storage detected"
            storageSize={formatBytes(totalSize)}
            volumeCount={scoped.length.toLocaleString()}
          />
        ) : (
          metrics.map((metric) => (
            <StorageMetric
              key={metric.title}
              title={metric.title}
              storageSize={formatBytes(metric.size)}
              volumeCount={metric.count.toLocaleString()}
            />
          ))
        )}
      </div>
      {scoped.length > 0 ? <SavingOpportunityCard className="pt-3" /> : null}
    </SidebarCard>
  );
}

function StorageMetric({ title, storageSize, volumeCount }: StorageMetricProps) {
  return (
    <div className={clsx('min-w-0 flex flex-col gap-3.5 rounded-lg')}>
      <Typography variant="p3-reg" color="text-primary-900" fontWeight={500}>
        {title}
      </Typography>
      <div className="flex items-center gap-3 min-w-0 flex-wrap">
        <Typography
          variant="H3"
          fontWeight={600}
          color="text-primary-900"
          className="font-family-outfit tracking-wide leading-6 whitespace-nowrap text-2xl"
        >
          {storageSize}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          color="text-gray-700"
          className="px-1.5 py-1 bg-bg rounded whitespace-nowrap shrink-0 font-family-inter"
        >
          {volumeCount} Vol.
        </Typography>
      </div>
    </div>
  );
}
